import { datumUsageNew, DatumHorizontalUsageType } from "building-types/datums/datumUsageTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { InteriorMaterial } from "building-types/materials/interiorPaletteTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type {
  WallAssembly,
  WallAssemblyCladdingLayer,
  WallAssemblyAirGapLayer,
  WallAssemblyExteriorFinishLayer,
  WallAssemblyFinishLayer,
  WallAssemblyFoundationCatalogLayer,
  WallAssemblyInteriorFinishLayer,
  WallAssemblyLayer,
  WallAssemblyLayerConstructorParams,
  WallAssemblyLayerDomain,
  WallAssemblyMaterialCatalogLayer,
  WallAssemblyMaterialCatalogLayerBase,
  WallAssemblyMaterialCatalogLayerType,
  WallAssemblyMembraneLayer,
  WallAssemblyStructuralLayer,
  WallAssemblySubstrateLayer,
  WallAssemblyThermalLayer,
  WallAssemblyLayerParentHandle,
} from "building-types/walls/wallAssemblyTypes";
import type {
  WallDescriptor,
  WallSection,
  WallSectionSide,
  WallSideDescriptor,
} from "building-types/walls/wallDesciptorTypes";
import { StructuralAssemblyType, WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import type { StructuralMaterialTable, WallMaterial } from "building-types/walls/wallMaterialTypes";
import { WallMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import type { WallType } from "building-types/walls/wallType";
import type { WallPath } from "building-types/walls/wallTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { HackStartDatumIdsUntilAllFacesAreRemapped } from "doc/utils/datumTools";
import {
  isInteriorCladdingLayer,
  isExteriorCladdingLayer,
  isSubstrateLayer,
  isInteriorFinishLayer,
  isExteriorFinishLayer,
} from "doc/utils/wallAssemblyTools";
import { defaultWallMaterialIdByType } from "doc/utils/wallMaterialTools";
import { pathPriorityOpposite } from "doc/utils/wallTools";
import type { UVTransformation } from "geom/uv";
import { vec2Dist } from "geom/vec2";
import type { WallDatumAbstractionProvider } from "../datums/datumAbstractionProviders";
import { wallSectionSideCanApplyFacade } from "../walls/wallSectionTools";
import { defaultWallAssemblyLayerDomain } from "./wallAssemblyDefaults";

interface WallAssemblyMaterialCatalogLayerParams extends Partial<WallAssemblyLayerDomain> {
  wallMaterials: StructuralMaterialTable;
  type: WallAssemblyMaterialCatalogLayerType;
  wallMaterialId: string;
  roomId?: string;
  layerSource?: WallAssemblyLayer["layerSource"];
  parentChain?: WallAssemblyLayerParentHandle[];
}

/**
 * Base WallAssemblyMaterialCatalog layer constructor.
 * This function is for internal use only within wallAssembly and wallAssemblyOverride
 */
export function wallAssemblyMaterialCatalogLayerNew({
  lowerDatumUsage = defaultWallAssemblyLayerDomain.lowerDatumUsage,
  upperDatumUsage = defaultWallAssemblyLayerDomain.upperDatumUsage,
  startDatumUsage = defaultWallAssemblyLayerDomain.startDatumUsage,
  endDatumUsage = defaultWallAssemblyLayerDomain.endDatumUsage,
  type,
  wallMaterialId,
  roomId,
  layerSource,
  parentChain,
}: Omit<WallAssemblyMaterialCatalogLayerParams, "wallMaterials">): WallAssemblyMaterialCatalogLayer &
  WallAssemblyMaterialCatalogLayerBase<typeof type> {
  return {
    type,
    wallMaterialId,
    lowerDatumUsage,
    upperDatumUsage,
    startDatumUsage,
    endDatumUsage,
    roomId,
    layerSource,
    parentChain,
  };
}

export function wallAssemblyMaterialCatalogLayerAssertMaterial(params: WallAssemblyMaterialCatalogLayerParams) {
  const defaultMtlByType = defaultWallMaterialIdByType[params.type];

  const wallMaterial =
    params.wallMaterials[params.wallMaterialId] ??
    (defaultMtlByType !== undefined ? params.wallMaterials[defaultMtlByType] : undefined);
  if (!(wallMaterial.type === params.type)) {
    console.assert(false);
  }

  return wallAssemblyMaterialCatalogLayerNew(params);
}

export function wallAssemblyStructuralLayerNew(
  params: Omit<WallAssemblyMaterialCatalogLayerParams, "type">,
): WallAssemblyStructuralLayer {
  return wallAssemblyMaterialCatalogLayerAssertMaterial({
    ...params,
    type: WallMaterialType.STRUCTURE,
  }) as WallAssemblyStructuralLayer;
}

export function wallAssemblySubstrateLayerNew(
  params: Omit<WallAssemblyMaterialCatalogLayerParams, "type">,
): WallAssemblySubstrateLayer {
  return wallAssemblyMaterialCatalogLayerAssertMaterial({
    ...params,
    type: WallMaterialType.SUBSTRATE,
  }) as WallAssemblySubstrateLayer;
}

function wallAssemblyMembraneLayerNew(
  params: Omit<WallAssemblyMaterialCatalogLayerParams, "type">,
): WallAssemblyMembraneLayer {
  return wallAssemblyMaterialCatalogLayerAssertMaterial({
    ...params,
    type: WallMaterialType.MEMBRANE,
  }) as WallAssemblyMembraneLayer;
}

function wallAssemblyThermalLayerNew(
  params: Omit<WallAssemblyMaterialCatalogLayerParams, "type">,
): WallAssemblyThermalLayer {
  return wallAssemblyMaterialCatalogLayerAssertMaterial({
    ...params,
    type: WallMaterialType.THERMAL,
  }) as WallAssemblyThermalLayer;
}

interface WallAssemblyExteriorFinishLayerParams extends Partial<WallAssemblyLayerDomain> {
  finishMaterial: ExteriorMaterial;
  materialTransformation?: UVTransformation;
  materialThickness?: number;
  elevationNoteEnabled?: boolean;
  elevationNote?: string;
  roomId?: string;
  layerSource?: WallAssemblyLayer["layerSource"];
  parentChain?: WallAssemblyLayerParentHandle[];
}

export function wallAssemblyExteriorFinishLayerNew({
  lowerDatumUsage = defaultWallAssemblyLayerDomain.lowerDatumUsage,
  upperDatumUsage = defaultWallAssemblyLayerDomain.upperDatumUsage,
  startDatumUsage = defaultWallAssemblyLayerDomain.startDatumUsage,
  endDatumUsage = defaultWallAssemblyLayerDomain.endDatumUsage,
  finishMaterial,
  materialTransformation,
  materialThickness = 0,
  elevationNoteEnabled,
  elevationNote,
  roomId,
  layerSource,
  parentChain,
}: WallAssemblyExteriorFinishLayerParams): WallAssemblyExteriorFinishLayer {
  return {
    type: WallMaterialType.EXTERIOR_FINISH,
    finishMaterial,
    lowerDatumUsage,
    upperDatumUsage,
    startDatumUsage,
    endDatumUsage,
    materialTransformation,
    materialThickness,
    elevationNoteEnabled,
    elevationNote,
    roomId,
    layerSource,
    parentChain,
  };
}

interface WallAssemblyInteriorFinishLayerParams extends Partial<WallAssemblyLayerDomain> {
  finishMaterial: InteriorMaterial;
  roomId?: string;
  layerSource?: WallAssemblyLayer["layerSource"];
  parentChain?: WallAssemblyLayerParentHandle[];
}

export function wallAssemblyInteriorFinishLayerNew({
  lowerDatumUsage = defaultWallAssemblyLayerDomain.lowerDatumUsage,
  upperDatumUsage = defaultWallAssemblyLayerDomain.upperDatumUsage,
  startDatumUsage = defaultWallAssemblyLayerDomain.startDatumUsage,
  endDatumUsage = defaultWallAssemblyLayerDomain.endDatumUsage,
  finishMaterial,
  roomId,
  layerSource,
  parentChain,
}: WallAssemblyInteriorFinishLayerParams): WallAssemblyInteriorFinishLayer {
  return {
    type: WallMaterialType.INTERIOR_FINISH,
    finishMaterial,
    lowerDatumUsage,
    upperDatumUsage,
    startDatumUsage,
    endDatumUsage,
    roomId,
    layerSource,
    parentChain,
  };
}

interface WalllAssemblyFoundationLayerParams extends Partial<WallAssemblyLayerDomain> {
  foundationComponentId: string;
}

export function wallAssemblyFoundationLayerNew({
  lowerDatumUsage = defaultWallAssemblyLayerDomain.lowerDatumUsage,
  upperDatumUsage = defaultWallAssemblyLayerDomain.upperDatumUsage,
  startDatumUsage = defaultWallAssemblyLayerDomain.startDatumUsage,
  endDatumUsage = defaultWallAssemblyLayerDomain.endDatumUsage,
  foundationComponentId,
}: WalllAssemblyFoundationLayerParams): WallAssemblyFoundationCatalogLayer {
  return {
    type: WallMaterialType.FOUNDATION,
    foundationComponentId,
    lowerDatumUsage,
    upperDatumUsage,
    startDatumUsage,
    endDatumUsage,
  };
}

interface WallAssemblyAirGapLayerParams extends Partial<WallAssemblyLayerDomain> {
  airGapThickness: number;
  layerSource?: WallAssemblyLayer["layerSource"];
}

/** System-generated only — not exposed in user-facing wall assembly operators. */
export function wallAssemblyAirGapLayerNew({
  lowerDatumUsage = defaultWallAssemblyLayerDomain.lowerDatumUsage,
  upperDatumUsage = defaultWallAssemblyLayerDomain.upperDatumUsage,
  startDatumUsage = defaultWallAssemblyLayerDomain.startDatumUsage,
  endDatumUsage = defaultWallAssemblyLayerDomain.endDatumUsage,
  airGapThickness,
  layerSource,
}: WallAssemblyAirGapLayerParams): WallAssemblyAirGapLayer {
  return {
    type: WallMaterialType.AIRGAP,
    airGapThickness,
    lowerDatumUsage,
    upperDatumUsage,
    startDatumUsage,
    endDatumUsage,
    layerSource,
  };
}

export function wallAssemblyLayerNewFromConstructor(layer: WallAssemblyLayerConstructorParams) {
  switch (layer.type) {
    case WallMaterialType.FOUNDATION: {
      return wallAssemblyFoundationLayerNew(layer);
    }
    case WallMaterialType.STRUCTURE:
    case WallMaterialType.SUBSTRATE:
    case WallMaterialType.MEMBRANE:
    case WallMaterialType.THERMAL: {
      return wallAssemblyMaterialCatalogLayerNew(layer);
    }
    case WallMaterialType.INTERIOR_FINISH: {
      return wallAssemblyInteriorFinishLayerNew(layer);
    }
    case WallMaterialType.EXTERIOR_FINISH: {
      return wallAssemblyExteriorFinishLayerNew(layer);
    }
    default: {
      assertUnreachable(layer);
    }
  }
}

export function wallAssemblyNew({
  labels = {},
  structuralLayers = [],
  structuralPriority = PathPriority.NONE,
  structuralOffset = 0,
  leftCladdingLayers = [],
  rightCladdingLayers = [],
  extraLeftLayers = [],
  extraRightLayers = [],
  foundationLayers = [],
}: Partial<WallAssembly> = {}): WallAssembly {
  return {
    labels,
    foundationLayers,
    structuralLayers,
    structuralPriority,
    structuralOffset,
    leftCladdingLayers,
    rightCladdingLayers,
    extraLeftLayers,
    extraRightLayers,
  };
}

export function wallAssemblyOpposite(wallAssembly: WallAssembly): WallAssembly {
  return {
    labels: wallAssembly.labels,
    structuralLayers: wallAssembly.structuralLayers.map((x) => wallAssemblyLayerCopy(x)),
    structuralPriority: pathPriorityOpposite(wallAssembly.structuralPriority),
    structuralOffset: -wallAssembly.structuralOffset,
    foundationLayers: wallAssembly.foundationLayers.map((x) => wallAssemblyLayerCopy(x)),
    leftCladdingLayers: wallAssembly.rightCladdingLayers.map((x) => wallAssemblyLayerCopy(x)),
    rightCladdingLayers: wallAssembly.leftCladdingLayers.map((x) => wallAssemblyLayerCopy(x)),
    extraLeftLayers: wallAssembly.extraRightLayers.map((x) => wallAssemblyLayerCopy(x)),
    extraRightLayers: wallAssembly.extraLeftLayers.map((x) => wallAssemblyLayerCopy(x)),
  };
}

export function wallAssemblyLayerCopy<T extends WallAssemblyLayer>(wallAssemblyLayer: T): T {
  return {
    ...wallAssemblyLayer,
  };
}

interface MakeWallAssemblyOptions {
  wallMaterials: StructuralMaterialTable;
  wallSection: WallSection;
  wallDescriptor: WallDescriptor;
  wallType: Partial<WallType>;
}

export function makeWallAssemblyFromWallType({
  wallMaterials,
  wallSection,
  wallDescriptor,
  wallType,
}: MakeWallAssemblyOptions): WallAssembly {
  const {
    structuralMaterialId,
    exteriorSubstrateMaterialId: substrateMaterialId,
    membraneMaterialId,
    thermalMaterialId,
    foundationMaterialId,
  } = wallType;

  const { leftSide, rightSide } = wallDescriptor;
  const { leftSide: leftSectionSide, rightSide: rightSectionSide } = wallSection;

  const structuralLayers: WallAssemblyStructuralLayer[] = [];
  const leftCladdingLayers: WallAssemblyCladdingLayer[] = [];
  const rightCladdingLayers: WallAssemblyCladdingLayer[] = [];
  const foundationLayers: WallAssemblyFoundationCatalogLayer[] = [];

  const addCladdingLayers = (
    layerTarget: WallAssemblyCladdingLayer[],
    wallSide: WallSideDescriptor,
    wallSectionSide: WallSectionSide,
  ) => {
    /**
     * @NOTE - for now, any cladding layer not from the wall is defined by a facade.
     */
    if (
      wallSectionSide.structure === StructuralAssemblyType.FOUNDATION ||
      // wallSectionSide.structure === StructuralAssemblyType.ROOF ||
      wallSectionSide.structure === StructuralAssemblyType.NONE
    ) {
      return;
    }

    const { wallExteriorMaterial } = wallSide;

    /**
     * We only need to do this for the finish layers not created by an override,
     * so that we generate a face that encompasses the whole substrate domain.
     * @TODO - remove after all wall assembly finish layers are mapped to the wall domain
     */

    switch (wallSectionSide.cladding) {
      case WallCladdingType.EXTERIOR: {
        if (substrateMaterialId) {
          layerTarget.push(wallAssemblySubstrateLayerNew({ wallMaterials, wallMaterialId: substrateMaterialId }));
        }
        if (membraneMaterialId) {
          layerTarget.push(wallAssemblyMembraneLayerNew({ wallMaterials, wallMaterialId: membraneMaterialId }));
        }
        if (thermalMaterialId) {
          layerTarget.push(wallAssemblyThermalLayerNew({ wallMaterials, wallMaterialId: thermalMaterialId }));
        }
        if (wallExteriorMaterial) {
          layerTarget.push(
            wallAssemblyExteriorFinishLayerNew({
              finishMaterial: wallExteriorMaterial,
              startDatumUsage: datumUsageNew(
                DatumHorizontalUsageType.WALL_HORIZONTAL,
                HackStartDatumIdsUntilAllFacesAreRemapped.WALL_START,
              ),
              endDatumUsage: datumUsageNew(
                DatumHorizontalUsageType.WALL_HORIZONTAL,
                HackStartDatumIdsUntilAllFacesAreRemapped.WALL_END,
              ),
            }),
          );
        }
        break;
      }
      case WallCladdingType.INTERIOR: {
        layerTarget.push(
          wallAssemblyInteriorFinishLayerNew({
            finishMaterial: "wallInterior0",
            startDatumUsage: datumUsageNew(
              DatumHorizontalUsageType.WALL_HORIZONTAL,
              HackStartDatumIdsUntilAllFacesAreRemapped.WALL_START,
            ),
            endDatumUsage: datumUsageNew(
              DatumHorizontalUsageType.WALL_HORIZONTAL,
              HackStartDatumIdsUntilAllFacesAreRemapped.WALL_END,
            ),
          }),
        );
        break;
      }
      case WallCladdingType.NONE:
      default: {
        break;
      }
    }
  };

  const addStructuralLayers = (
    foundationLayerTarget: WallAssemblyFoundationCatalogLayer[],
    framingLayerTarget: WallAssemblyStructuralLayer[],
  ) => {
    /**
     * @NOTE - for the time being we address all non-foundation structure by adding a wall structural layer
     */
    if (
      leftSectionSide.structure === StructuralAssemblyType.WALL ||
      rightSectionSide.structure === StructuralAssemblyType.WALL
    ) {
      if (structuralMaterialId) {
        framingLayerTarget.push(
          wallAssemblyStructuralLayerNew({ wallMaterials, wallMaterialId: structuralMaterialId }),
        );
      }
    }
    /**
     * @TODO - this can become more nuanced because we can use structural information
     * from the wall section (i.e Floor, Ceiling, Foundation, etc...)
     */
    if (
      leftSectionSide.structure === StructuralAssemblyType.FOUNDATION ||
      rightSectionSide.structure === StructuralAssemblyType.FOUNDATION
    ) {
      if (foundationMaterialId) {
        foundationLayerTarget.push(wallAssemblyFoundationLayerNew({ foundationComponentId: foundationMaterialId }));
      }
    }
  };

  addStructuralLayers(foundationLayers, structuralLayers);
  addCladdingLayers(leftCladdingLayers, leftSide, leftSectionSide);
  addCladdingLayers(rightCladdingLayers, rightSide, rightSectionSide);

  return wallAssemblyNew({
    structuralLayers,
    foundationLayers,
    leftCladdingLayers,
    rightCladdingLayers,
  });
}

function mergeWallAssemblyLayers(a: WallAssemblyLayer[], b: WallAssemblyLayer[]) {
  const layers = new Array<WallAssemblyLayer>(a.length + b.length);
  let i = 0;
  for (let l = a.length; i < l; i++) {
    layers[i] = a[i];
  }
  for (let j = 0, l = b.length; j < l; j++) {
    layers[i + j] = b[j];
  }
  return layers;
}

export function getWallAssemblyCladdingLayers(
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): WallAssemblyCladdingLayer[] {
  const { leftSide, rightSide } = wallSection;
  const claddingType = isLeftSide ? leftSide.cladding : rightSide.cladding;
  if (claddingType === WallCladdingType.NONE) {
    return [];
  }

  const claddingLayers = isLeftSide
    ? mergeWallAssemblyLayers(wallAssembly.leftCladdingLayers, wallAssembly.extraLeftLayers)
    : mergeWallAssemblyLayers(wallAssembly.rightCladdingLayers, wallAssembly.extraRightLayers);

  if (claddingType === WallCladdingType.INTERIOR) {
    return claddingLayers.filter(isInteriorCladdingLayer);
  } else {
    return claddingLayers.filter(isExteriorCladdingLayer);
  }
}

export function getWallAssemblySubstrateLayers(
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): WallAssemblyCladdingLayer[] {
  const { leftSide, rightSide } = wallSection;
  const claddingType = isLeftSide ? leftSide.cladding : rightSide.cladding;
  if (claddingType === WallCladdingType.NONE) {
    return [];
  }

  const claddingLayers = getWallAssemblyCladdingLayers(wallAssembly, wallSection, isLeftSide);
  return claddingLayers.filter(isSubstrateLayer);
}

/**
 * @NOTE WALLS V2
 * This is a wrapper function that encapsulates changes made to the wall assembly
 * to accomodate Walls v2
 */
export function getWallAssemblySubstrateLayers2(
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): WallAssemblyLayer[] {
  const { leftCladdingLayers, rightCladdingLayers } = wallAssembly;
  const claddingLayers = isLeftSide ? leftCladdingLayers : rightCladdingLayers;
  if (claddingLayers.some((l) => l.layerSource?.type === "WALL_PLANE_RULE")) {
    return [];
  }

  return getWallAssemblySubstrateLayers(wallAssembly, wallSection, isLeftSide);
}

export function getWallAssemblyFinishLayers(
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): WallAssemblyFinishLayer[] {
  const { leftSide, rightSide } = wallSection;
  const claddingType = isLeftSide ? leftSide.cladding : rightSide.cladding;
  if (claddingType === WallCladdingType.NONE) {
    return [];
  }

  const claddingLayers = getWallAssemblyCladdingLayers(wallAssembly, wallSection, isLeftSide);
  if (claddingType === WallCladdingType.INTERIOR) {
    return claddingLayers.filter(isInteriorFinishLayer);
  } else {
    return claddingLayers.filter(isExteriorFinishLayer);
  }
}

/**
 * @NOTE WALLS V2
 * This is a wrapper function that encapsulates changes made to the wall assembly
 * to accomodate Walls v2
 */
export function getWallAssemblyFinishLayers2(
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): WallAssemblyLayer[] {
  const { leftCladdingLayers, rightCladdingLayers } = wallAssembly;
  const claddingLayers = isLeftSide ? leftCladdingLayers : rightCladdingLayers;
  if (claddingLayers.some((l) => l.layerSource?.type === "WALL_PLANE_RULE")) {
    // This is the internal check that says that this is a wallsv2 wall assembly
    // which means that structural layers may have inserted finishes into the core
    return claddingLayers;
  }

  return getWallAssemblyFinishLayers(wallAssembly, wallSection, isLeftSide);
}

function getWallAssemblyInternalLayerMaterial(
  wallMaterials: StructuralMaterialTable,
  wallAssemblyLayer: WallAssemblyMaterialCatalogLayer,
): WallMaterial | undefined {
  const defaultMaterialId = defaultWallMaterialIdByType[wallAssemblyLayer.type];
  return (wallMaterials[wallAssemblyLayer.wallMaterialId] ??
    (defaultMaterialId !== undefined ? wallMaterials[defaultMaterialId] : undefined)) as WallMaterial | undefined;
}

export function accumWallAssemblyLayerThickness(
  wallMaterials: StructuralMaterialTable,
  wallAssemblyLayers: WallAssemblyLayer[],
): number {
  return wallAssemblyLayers.reduce((thickness, layer) => {
    const layerThickness = getWallAssemblyLayerThickness(wallMaterials, layer);
    if (layerThickness > 0) {
      thickness += layerThickness;
    }
    return thickness;
  }, 0);
}

export function getWallAssemblyLayerThickness(
  wallMaterials: StructuralMaterialTable,
  wallAssemblyLayer: WallAssemblyLayer,
): number {
  let layerThickness = 0;
  switch (wallAssemblyLayer.type) {
    case WallMaterialType.STRUCTURE:
    case WallMaterialType.SUBSTRATE: {
      const wallMaterial = getWallAssemblyInternalLayerMaterial(wallMaterials, wallAssemblyLayer);
      assertIsNotUndefined(wallMaterial);
      layerThickness = wallMaterial.thickness;
      break;
    }
    case WallMaterialType.EXTERIOR_FINISH: {
      layerThickness = wallAssemblyLayer.materialThickness ?? 0;
      break;
    }
    case WallMaterialType.AIRGAP: {
      layerThickness = wallAssemblyLayer.airGapThickness;
      break;
    }
    default: {
      break;
    }
  }
  return layerThickness;
}

export function getWallAssemblyStructuralThickness(
  wallMaterials: StructuralMaterialTable,
  wallAssembly: WallAssembly,
): number {
  return accumWallAssemblyLayerThickness(wallMaterials, wallAssembly.structuralLayers);
}

export function getWallAssemblySubstrateThickness(
  wallMaterials: StructuralMaterialTable,
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): number {
  const substrateLayers = getWallAssemblySubstrateLayers2(wallAssembly, wallSection, isLeftSide);
  return accumWallAssemblyLayerThickness(wallMaterials, substrateLayers);
}

export function getWallAssemblyFinishThickness(
  wallMaterials: StructuralMaterialTable,
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): number {
  const finishLayers = getWallAssemblyFinishLayers2(wallAssembly, wallSection, isLeftSide);
  return accumWallAssemblyLayerThickness(wallMaterials, finishLayers);
}

export function getWallAssemblyFinishThicknessAtWallPathParam(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallMaterials: StructuralMaterialTable,
  wallAssembly: WallAssembly,
  wallPath: WallPath,
  wallPathParam: number,
  wallSection: WallSection,
  isLeftSide: boolean,
): number {
  const finishLayers = getWallAssemblyFinishLayers2(wallAssembly, wallSection, isLeftSide).filter((layer) => {
    return wallAssemblyLayerOverlapsWallPathParam(wallDatumAbstractionProvider, wallPath, wallPathParam, layer);
  });

  return accumWallAssemblyLayerThickness(wallMaterials, finishLayers);
}

export function getWallAssemblyCladdingThickness(
  wallMaterials: StructuralMaterialTable,
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  isLeftSide: boolean,
): number {
  const claddingLayers = getWallAssemblyCladdingLayers(wallAssembly, wallSection, isLeftSide);
  return accumWallAssemblyLayerThickness(wallMaterials, claddingLayers);
}

export function wallAssemblyCanApplyLayer(
  level: Level,
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallPath: WallPath,
  wallDescriptor: WallDescriptor,
  wallSection: WallSection,
  wallAssemblyLayer: WallAssemblyLayer,
  isLeftSide: boolean,
) {
  if (
    wallAssemblyLayer.type === WallMaterialType.EXTERIOR_FINISH &&
    wallAssemblyLayer.layerSource?.type === "FACADE_SPAN_STRATUM" &&
    !wallSectionSideCanApplyFacade(wallDescriptor, wallSection, isLeftSide)
  ) {
    return false;
  }

  const { componentInstanceChain } = level;

  const wallSide = isLeftSide ? wallDescriptor.leftSide : wallDescriptor.rightSide;
  const wallOppSide = isLeftSide ? wallDescriptor.rightSide : wallDescriptor.leftSide;

  const { topOffset, bottomOffset, startOffset, endOffset } = wallSection;
  const wallPathLen = vec2Dist(wallPath.startX, wallPath.startY, wallPath.endX, wallPath.endY);

  const wallLayerSide = wallAssemblyLayer.roomId === wallSide.wallRoom.room?.id ? wallSide : wallOppSide;
  const { lowerDatumUsage, upperDatumUsage, startDatumUsage, endDatumUsage } = wallAssemblyLayer;

  const lowerDatumAbstraction = wallDatumAbstractionProvider.getWallVerticalDatumAbstraction(
    lowerDatumUsage,
    "WALL_BOTTOM",
    componentInstanceChain,
  );
  const upperDatumAbstraction = wallDatumAbstractionProvider.getWallVerticalDatumAbstraction(
    upperDatumUsage,
    "WALL_TOP",
    componentInstanceChain,
  );

  const layerBottomOffset = lowerDatumAbstraction?.isBaseVerticalDatumType(true, lowerDatumUsage.offset)
    ? bottomOffset // TODO - remove this when we remove "base_wall_bottom"
    : wallDatumAbstractionProvider.resolveWallVerticalDatumAbstractionRelativeToWallSide(
        lowerDatumAbstraction,
        wallLayerSide,
        lowerDatumUsage.offset,
      );

  const layerTopOffset = upperDatumAbstraction?.isBaseVerticalDatumType(false, upperDatumUsage.offset)
    ? topOffset // TODO - remove this when we remove "base_wall_top"
    : wallDatumAbstractionProvider.resolveWallVerticalDatumAbstractionRelativeToWallSide(
        upperDatumAbstraction,
        wallLayerSide,
        upperDatumUsage.offset,
      );

  if (topOffset <= layerBottomOffset || layerTopOffset <= bottomOffset) {
    return false;
  }

  const startDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(
    startDatumUsage,
    "WALL_START",
  );
  const endDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(endDatumUsage, "WALL_END");

  const layerStartOffset =
    startDatumAbstraction.resolveHorizontalDatum(wallPathLen, startDatumUsage.offset) / wallPathLen;
  const layerEndOffset = endDatumAbstraction.resolveHorizontalDatum(wallPathLen, endDatumUsage.offset) / wallPathLen;

  if (endOffset <= layerStartOffset || layerEndOffset <= startOffset) {
    return false;
  }

  return true;
}

function wallAssemblyLayerOverlapsWallPathParam(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallPath: WallPath,
  wallPathParam: number,
  wallAssemblyLayer: WallAssemblyLayer,
) {
  const wallPathLen = vec2Dist(wallPath.startX, wallPath.startY, wallPath.endX, wallPath.endY);

  const { startDatumUsage, endDatumUsage } = wallAssemblyLayer;

  const startDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(
    startDatumUsage,
    "WALL_START",
  );
  const endDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(endDatumUsage, "WALL_END");

  const layerStartOffset =
    startDatumAbstraction.resolveHorizontalDatum(wallPathLen, startDatumUsage.offset) / wallPathLen;
  const layerEndOffset = endDatumAbstraction.resolveHorizontalDatum(wallPathLen, endDatumUsage.offset) / wallPathLen;

  return layerStartOffset <= wallPathParam && wallPathParam <= layerEndOffset;
}
